import { z } from 'zod';

export const missionModeSchema = z.enum([
  'learn',
  'teach',
  'explain',
  'analyze',
  'create',
]);

export const designMissionSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  mode: missionModeSchema,
  description: z.string().default(''),
  definitionOfDone: z.array(z.string().min(1)).default([]),
  constraints: z.array(z.string().min(1)).default([]),
}).strict();

export const confirmedFactSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  value: z.string().min(1),
  source: z.literal('user'),
  sourceMessageId: z.string().min(1).optional(),
  questionId: z.string().min(1).optional(),
  confirmedAt: z.string().datetime(),
}).strict();

export const designQuestionSchema = z.object({
  id: z.string().min(1),
  question: z.string().min(1),
  critical: z.boolean().default(false),
  status: z.enum(['open', 'answered', 'superseded']).default('open'),
  answerFactId: z.string().min(1).optional(),
  askedAt: z.string().datetime(),
  resolvedAt: z.string().datetime().optional(),
}).strict();

export const analysisSummarySchema = z.object({
  id: z.string().min(1),
  goalId: z.string().min(1).optional(),
  summary: z.string().min(1),
  evidenceFactIds: z.array(z.string().min(1)).default([]),
  createdAt: z.string().datetime(),
}).strict();

export const designDecisionSchema = z.object({
  id: z.string().min(1),
  goalId: z.string().min(1).optional(),
  title: z.string().min(1),
  decision: z.string().min(1),
  rationaleSummary: z.string().default(''),
  status: z.enum(['proposed', 'confirmed', 'revised']).default('proposed'),
  createdAt: z.string().datetime(),
}).strict();

export const designGoalStatusSchema = z.enum([
  'pending',
  'active',
  'awaiting_user',
  'analyzing',
  'implementing',
  'validating',
  'blocked',
  'satisfied',
  'skipped',
]);

export const designGoalSchema = z.object({
  id: z.string().min(1),
  domain: z.string().min(1),
  parentId: z.string().min(1).optional(),
  dependsOn: z.array(z.string().min(1)).default([]),
  order: z.number().int().nonnegative(),
  title: z.string().min(1),
  description: z.string().default(''),
  status: designGoalStatusSchema,
  required: z.boolean().default(false),
  completionCriteria: z.array(z.string().min(1)).default([]),
  expectedOutputs: z.array(z.string().min(1)).default([]),
  evidenceRefs: z.array(z.string().min(1)).default([]),
  openQuestionIds: z.array(z.string().min(1)).default([]),
  requiresUserConfirmation: z.boolean().default(false),
  transitionReason: z.string().optional(),
  updatedAt: z.string().datetime(),
}).strict();

export const workflowPlanSchema = z.object({
  id: z.string().min(1),
  goals: z.array(designGoalSchema).default([]),
  activeGoalId: z.string().min(1).optional(),
  suggestedNextGoalIds: z.array(z.string().min(1)).default([]),
  updatedAt: z.string().datetime(),
}).strict();

export const readinessCheckSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  status: z.enum(['unknown', 'partial', 'sufficient', 'not_applicable']),
  summary: z.string().default(''),
}).strict();

export const designReadinessSchema = z.object({
  target: z.string().min(1),
  ready: z.boolean(),
  blockers: z.array(z.string().min(1)).default([]),
  checks: z.array(readinessCheckSchema).default([]),
}).strict();

export const designSessionStateSchema = z.object({
  id: z.string().min(1),
  revision: z.number().int().nonnegative(),
  conversationId: z.string().min(1).optional(),
  projectId: z.string().min(1).optional(),
  mission: designMissionSchema.optional(),
  plan: workflowPlanSchema,
  confirmedFacts: z.array(confirmedFactSchema).default([]),
  questions: z.array(designQuestionSchema).default([]),
  analysisSummaries: z.array(analysisSummarySchema).default([]),
  decisions: z.array(designDecisionSchema).default([]),
  readiness: designReadinessSchema,
  updatedAt: z.string().datetime(),
}).strict().superRefine((state, context) => {
  const goalIds = new Set(state.plan.goals.map(goal => goal.id));
  const questionIds = new Set(state.questions.map(question => question.id));
  const factIds = new Set(state.confirmedFacts.map(fact => fact.id));
  const goalById = new Map(state.plan.goals.map(goal => [goal.id, goal]));
  const questionById = new Map(
    state.questions.map(question => [question.id, question]),
  );
  const factById = new Map(state.confirmedFacts.map(fact => [fact.id, fact]));

  function reportDuplicateIds(
    items: Array<{ id: string }>,
    path: string,
    label: string,
  ): void {
    const seen = new Set<string>();
    for (const [index, item] of items.entries()) {
      if (seen.has(item.id)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...path.split('.'), index, 'id'],
          message: `${label}编号不能重复`,
        });
      }
      seen.add(item.id);
    }
  }

  reportDuplicateIds(state.plan.goals, 'plan.goals', '目标');
  reportDuplicateIds(state.questions, 'questions', '问题');
  reportDuplicateIds(state.confirmedFacts, 'confirmedFacts', '事实');
  reportDuplicateIds(state.analysisSummaries, 'analysisSummaries', '分析摘要');
  reportDuplicateIds(state.decisions, 'decisions', '设计决策');

  if (state.plan.activeGoalId && !goalIds.has(state.plan.activeGoalId)) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['plan', 'activeGoalId'],
      message: '当前目标不存在',
    });
  }

  for (const [index, goal] of state.plan.goals.entries()) {
    if (goal.parentId && !goalIds.has(goal.parentId)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['plan', 'goals', index, 'parentId'],
        message: '父目标不存在',
      });
    }
    if (goal.parentId === goal.id || goal.dependsOn.includes(goal.id)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['plan', 'goals', index],
        message: '目标不能依赖自身',
      });
    }
    for (const dependencyId of goal.dependsOn) {
      if (!goalIds.has(dependencyId)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['plan', 'goals', index, 'dependsOn'],
          message: `依赖目标不存在：${dependencyId}`,
        });
      }
    }

    const runningStatuses = new Set([
      'active',
      'awaiting_user',
      'analyzing',
      'implementing',
      'validating',
    ]);
    if (runningStatuses.has(goal.status)) {
      const incompleteDependency = goal.dependsOn.find((dependencyId) => {
        const dependency = goalById.get(dependencyId);
        return dependency
          && dependency.status !== 'satisfied'
          && dependency.status !== 'skipped';
      });
      if (incompleteDependency) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['plan', 'goals', index, 'status'],
          message: `前置目标尚未完成：${incompleteDependency}`,
        });
      }
    }
    for (const questionId of goal.openQuestionIds) {
      if (!questionIds.has(questionId)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['plan', 'goals', index, 'openQuestionIds'],
          message: `待确认问题不存在：${questionId}`,
        });
      }
      else if (questionById.get(questionId)?.status !== 'open') {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['plan', 'goals', index, 'openQuestionIds'],
          message: `目标引用的问题已经关闭：${questionId}`,
        });
      }
    }
    if (
      (goal.status === 'blocked' || goal.status === 'skipped')
      && !goal.transitionReason?.trim()
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['plan', 'goals', index, 'transitionReason'],
        message: '受阻或跳过目标必须说明原因',
      });
    }
  }

  for (const [index, question] of state.questions.entries()) {
    if (question.status === 'open') {
      if (question.answerFactId || question.resolvedAt) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['questions', index],
          message: '待回答问题不能包含回答或解决时间',
        });
      }
    }
    else if (!question.resolvedAt) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['questions', index, 'resolvedAt'],
        message: '已关闭问题必须记录解决时间',
      });
    }
    if (question.status === 'answered' && !question.answerFactId) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['questions', index, 'answerFactId'],
        message: '已回答问题必须关联用户事实',
      });
    }
    if (question.answerFactId && !factIds.has(question.answerFactId)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['questions', index, 'answerFactId'],
        message: '问题引用的回答事实不存在',
      });
    }
    const answerFact = question.answerFactId
      ? factById.get(question.answerFactId)
      : undefined;
    if (answerFact && answerFact.questionId !== question.id) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['questions', index, 'answerFactId'],
        message: '回答事实没有关联当前问题',
      });
    }
  }

  for (const [index, fact] of state.confirmedFacts.entries()) {
    if (fact.questionId && !questionIds.has(fact.questionId)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['confirmedFacts', index, 'questionId'],
        message: '事实引用的问题不存在',
      });
    }
  }

  for (const [index, analysis] of state.analysisSummaries.entries()) {
    if (analysis.goalId && !goalIds.has(analysis.goalId)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['analysisSummaries', index, 'goalId'],
        message: '分析摘要引用的目标不存在',
      });
    }
    for (const factId of analysis.evidenceFactIds) {
      if (!factIds.has(factId)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['analysisSummaries', index, 'evidenceFactIds'],
          message: `分析摘要引用的事实不存在：${factId}`,
        });
      }
    }
  }

  for (const [index, decision] of state.decisions.entries()) {
    if (decision.goalId && !goalIds.has(decision.goalId)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['decisions', index, 'goalId'],
        message: '设计决策引用的目标不存在',
      });
    }
  }

  for (const [index, goalId] of state.plan.suggestedNextGoalIds.entries()) {
    if (!goalIds.has(goalId)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['plan', 'suggestedNextGoalIds', index],
        message: `建议目标不存在：${goalId}`,
      });
    }
  }

  const dependencies = new Map(
    state.plan.goals.map(goal => [goal.id, goal.dependsOn]),
  );
  const visiting = new Set<string>();
  const visited = new Set<string>();
  function visit(goalId: string): boolean {
    if (visiting.has(goalId))
      return true;
    if (visited.has(goalId))
      return false;

    visiting.add(goalId);
    for (const dependencyId of dependencies.get(goalId) ?? []) {
      if (visit(dependencyId))
        return true;
    }
    visiting.delete(goalId);
    visited.add(goalId);
    return false;
  }

  if ([...goalIds].some(visit)) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['plan', 'goals'],
      message: '目标依赖不能形成循环',
    });
  }

  const parents = new Map(
    state.plan.goals.map(goal => [goal.id, goal.parentId]),
  );
  for (const goalId of goalIds) {
    const lineage = new Set<string>();
    let currentId: string | undefined = goalId;
    while (currentId) {
      if (lineage.has(currentId)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['plan', 'goals'],
          message: '父子目标不能形成循环',
        });
        break;
      }
      lineage.add(currentId);
      currentId = parents.get(currentId);
    }
  }
});

export type MissionMode = z.infer<typeof missionModeSchema>;
export type DesignMission = z.infer<typeof designMissionSchema>;
export type ConfirmedFact = z.infer<typeof confirmedFactSchema>;
export type DesignQuestion = z.infer<typeof designQuestionSchema>;
export type AnalysisSummary = z.infer<typeof analysisSummarySchema>;
export type DesignDecision = z.infer<typeof designDecisionSchema>;
export type DesignGoalStatus = z.infer<typeof designGoalStatusSchema>;
export type DesignGoal = z.infer<typeof designGoalSchema>;
export type WorkflowPlan = z.infer<typeof workflowPlanSchema>;
export type ReadinessCheck = z.infer<typeof readinessCheckSchema>;
export type DesignReadiness = z.infer<typeof designReadinessSchema>;
export type DesignSessionState = z.infer<typeof designSessionStateSchema>;

export function createEmptyDesignSession(
  id = 'design-session',
  projectId = 'visualization-project',
): DesignSessionState {
  const now = new Date().toISOString();
  return designSessionStateSchema.parse({
    id,
    revision: 0,
    projectId,
    plan: {
      id: `${id}-plan`,
      goals: [],
      suggestedNextGoalIds: [],
      updatedAt: now,
    },
    confirmedFacts: [],
    questions: [],
    analysisSummaries: [],
    decisions: [],
    readiness: {
      target: '明确下一步',
      ready: false,
      blockers: ['尚未描述可视化任务'],
      checks: [],
    },
    updatedAt: now,
  });
}
